import moment from 'moment';

import {
  TRANSITION_ACCEPT,
  TRANSITION_AUTO_ACCEPT,
  TRANSITION_CONFIRM_PAYMENT,
} from '../transaction';

// Transitions that mean "the listing is reserved for me on these dates". We
// treat confirm-payment as booked as well so a transaction that auto-confirmed
// straight to the accepted state still lights up the badge.
export const BOOKED_TRANSITIONS = [
  TRANSITION_ACCEPT,
  TRANSITION_AUTO_ACCEPT,
  TRANSITION_CONFIRM_PAYMENT,
];

/**
 * Walk the `included` array from a Flex transactions.query response and build
 * a lookup of booking-id → booking resource so each transaction can resolve
 * its booking dates without a nested .find() per hit.
 */
const indexBookings = (included) => {
  const byId = {};
  for (const resource of included || []) {
    if (resource?.type === 'booking' && resource?.id?.uuid) {
      byId[resource.id.uuid] = resource;
    }
  }
  return byId;
};

/**
 * Extract the canonical booking window. `displayStart` / `displayEnd` render
 * the range the user actually booked (timezone-shifted to match the listing
 * location); `start` / `end` are the raw UTC values. Prefer `display*` when
 * available — that's what the UI shows everywhere else (see InboxPage).
 */
const bookingWindow = (booking) => {
  const attrs = booking?.attributes || {};
  const start = attrs.displayStart || attrs.start;
  const end = attrs.displayEnd || attrs.end;
  if (!start || !end) return null;
  return { start, end };
};

/**
 * Given a list of transactions and the matching `included` resources, build
 * a map of listingId → array of booking windows. Multiple transactions on the
 * same listing are merged into a list of ranges so per-stop matching stays
 * O(ranges-per-listing) instead of walking every transaction each render.
 *
 * @param {Array} transactions
 * @param {Array} included
 * @returns {Record<string, { start: string, end: string }[]>}
 */
export const buildBookedMap = (transactions, included) => {
  const bookings = indexBookings(included);
  const map = {};
  for (const tx of transactions || []) {
    const listingId = tx?.relationships?.listing?.data?.id?.uuid;
    const bookingId = tx?.relationships?.booking?.data?.id?.uuid;
    if (!listingId || !bookingId) continue;
    const window = bookingWindow(bookings[bookingId]);
    if (!window) continue;
    if (!map[listingId]) map[listingId] = [];
    map[listingId].push(window);
  }
  return map;
};

/**
 * Test whether an itinerary stop overlaps any booking window on that listing.
 * A stop without both dates can never match — the UI surfaces that as
 * "needs dates" rather than "booked".
 *
 * Overlap condition: `bStart < stopEnd AND bEnd > stopStart`. Inclusive ends
 * would also flag a check-out-day = check-in-day match, but Flex booking
 * ranges are half-open (end is the checkout morning), so strict is right.
 *
 * @param {{ startDate: string | null, endDate: string | null, listingId: string }} stop
 * @param {Record<string, { start: string, end: string }[]>} bookedMap
 */
export const isStopBooked = (stop, bookedMap) => {
  if (!stop?.startDate || !stop?.endDate) return false;
  const ranges = bookedMap?.[stop.listingId];
  if (!ranges || ranges.length === 0) return false;
  const stopStart = moment(stop.startDate, 'YYYY-MM-DD', true);
  const stopEnd = moment(stop.endDate, 'YYYY-MM-DD', true);
  if (!stopStart.isValid() || !stopEnd.isValid()) return false;
  return ranges.some(({ start, end }) => {
    const bStart = moment(start);
    const bEnd = moment(end);
    return bStart.isBefore(stopEnd) && bEnd.isAfter(stopStart);
  });
};

export const STOP_STATUS_BOOKED = 'booked';
export const STOP_STATUS_NEEDS_DATES = 'needsDates';
export const STOP_STATUS_PLANNED = 'planned';

/**
 * Derive the badge state for an itinerary stop in Trip Mode.
 * - `needsDates` when either date is missing
 * - `booked` when a booking window overlaps the stop
 * - `planned` otherwise
 */
export const getStopStatus = (stop, bookedMap) => {
  if (!stop?.startDate || !stop?.endDate) return STOP_STATUS_NEEDS_DATES;
  return isStopBooked(stop, bookedMap) ? STOP_STATUS_BOOKED : STOP_STATUS_PLANNED;
};
